import { useTimer } from '../';
import { ApplicationFormStatus, useApplicationStatusQuery } from '../../graphql';
import { ApplicationSubmissionStatus } from '../../pages/ApplicationStatus/types';
import { useApplicationData } from '../../provider';
import { MAX_SYNC_STATUS_WAIT, getApplicantTypeAndTrackingIdFromUrl, instantApprovalProducts } from '../../utils';
import { getSubmissionStatus } from './helpers';

export const useGetApplicationSubmissionStatus = (): {
  status: ApplicationSubmissionStatus;
  time?: number;
} => {
  const { HouseholdDetails: householdDetails, quote } = useApplicationData(['HouseholdDetails', 'quote']);

  const hasJointApplicant = householdDetails?.hasJointApplicant;
  const productIsInstantApproval = instantApprovalProducts.includes(quote?.financialProduct?.id ?? '');

  //get application status
  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const { time, clearTimer } = useTimer({ max: MAX_SYNC_STATUS_WAIT, interval: 1000, direction: 'asc' });
  const { data: application } = useApplicationStatusQuery(
    { applicationId: trackingId },
    {
      // Only the instant-approval flow polls for a status transition (Submitted -> Approved/Declined).
      refetchInterval: data => {
        const status = data?.application?.status;
        if (
          productIsInstantApproval &&
          (!status || (status === ApplicationFormStatus.Submitted && time < MAX_SYNC_STATUS_WAIT))
        ) {
          return 5000;
        }
        return false;
      },

      useErrorBoundary: true,
      // Fetch the live status for every non-joint application (a single fetch for non-instant products,
      // polled for instant approval) so terminal states like Withdrawn are reflected regardless of
      // product type — not only in the instant-approval flow.
      enabled: !hasJointApplicant && time <= MAX_SYNC_STATUS_WAIT,
    },
  );

  const applicationStatus = application?.application?.status;
  const submissionStatus = getSubmissionStatus(productIsInstantApproval, !!hasJointApplicant, time, applicationStatus);

  if (submissionStatus !== ApplicationSubmissionStatus.FETCHING) {
    clearTimer();
  }

  return {
    status: submissionStatus,
    time,
  };
};
